import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction, SetURLSearchParams } from 'react-router-dom';

import type { Clinic } from '@app-types/auth';
import type { ToastMethods } from '@/hooks/useAppToast';
import type useAppModal from '@/hooks/useAppModal';
import type { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { runValidations, showValidationErrors } from '@/utils/patientValidation';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { clearLatestEditingPatientId } from '@shared/lib/latestEditingPatient';
import {
  applyLegacyPatientEditSearchParams,
  hasPatientEditHistorySearchParams,
} from '@pages/main/patient/utils/patientEditHistoryUrl';
import { transformPatientEditToFormData } from '@pages/main/patient/services';
import type { CrossSectionValidationResult } from './useCrossSectionValidation';
import type { PatientEditMutations, PatientSubmittingAction } from './usePatientEditPage.types';
import type { PatientFormData, PatientFormSchemaType } from '@pages/main/patient/types';
import {
  buildPatientFormPayloadForSubmit,
  normalizePatientData,
  toFormValues,
} from './usePatientEditPage.formValues';
import {
  BULK_HISTORY_NO_CHANGE_MESSAGE,
  buildExistingHistorySearchParamsFromForm,
  isBulkHistoryDraftUnchanged,
} from './usePatientEditPage.history';
import { validateProvisionalPatientForm } from './usePatientEditPage.provisionalValidation';

type AppModal = ReturnType<typeof useAppModal>;

interface UsePatientEditPageSaveActionsArgs {
  /**
   * Form values for a record — `toFormValues` plus the embedder's extra fields
   * (`options.extraFormFields`). Defaults to `toFormValues`.
   */
  toFormValues?: (data: PatientFormData) => PatientFormSchemaType;
  form: ReturnType<typeof useTrimmedForm<PatientFormSchemaType>>;
  mutations: PatientEditMutations;
  queryClient: QueryClient;
  navigate: NavigateFunction;
  modal: AppModal;
  toast: ToastMethods;
  clinic: Clinic | null;
  tenant?: string;
  id?: string;
  isEditMode: boolean;
  isNewHistoryMode: boolean;
  patientData: PatientFormData | null;
  initialFormValuesForGuard: PatientFormSchemaType | null;
  singleHistoryAvailableToMinus: string | undefined;
  singleHistoryQualificationDateYmd: string | undefined;
  historySearchParamsRef: MutableRefObject<URLSearchParams | undefined>;
  setHistorySearchParamsRef: MutableRefObject<SetURLSearchParams | undefined>;
  /**
   * `options.onSaved`, held in a ref like the other caller callbacks so a new
   * closure from the embedder does not re-create the save handlers.
   */
  onSavedRef: MutableRefObject<((patient: PatientFormData) => void) | undefined>;
  setPatientData: Dispatch<SetStateAction<PatientFormData | null>>;
  setInitialFormValuesForGuard: Dispatch<SetStateAction<PatientFormSchemaType | null>>;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  setSubmittingAction: Dispatch<SetStateAction<PatientSubmittingAction | null>>;
  setIsNewHistoryMode: Dispatch<SetStateAction<boolean>>;
  setSingleHistoryAvailableToMinus: Dispatch<SetStateAction<string | undefined>>;
  setSingleHistoryQualificationDateYmd: Dispatch<SetStateAction<string | undefined>>;
  syncOriginalCurrentIds: (data: PatientFormData) => void;
  clearFormChange: () => void;
  runAllValidations: () => Promise<CrossSectionValidationResult>;
  /** Embedder wording for the dialog messages; identity when absent. */
  overrideMessage?: (message: string) => string;
  /** Toast 「更新ができませんでした。」 after a cross-section dialog refuses the save; true when absent. */
  crossSectionErrorToast?: boolean;
}

export const usePatientEditPageSaveActions = ({
  toFormValues: toEditFormValues = toFormValues,
  form,
  mutations,
  queryClient,
  navigate,
  modal,
  toast,
  clinic,
  tenant,
  id,
  isEditMode,
  isNewHistoryMode,
  patientData,
  initialFormValuesForGuard,
  singleHistoryAvailableToMinus,
  singleHistoryQualificationDateYmd,
  historySearchParamsRef,
  setHistorySearchParamsRef,
  onSavedRef,
  setPatientData,
  setInitialFormValuesForGuard,
  setIsSubmitting,
  setSubmittingAction,
  setIsNewHistoryMode,
  setSingleHistoryAvailableToMinus,
  setSingleHistoryQualificationDateYmd,
  syncOriginalCurrentIds,
  clearFormChange,
  runAllValidations,
  overrideMessage,
  crossSectionErrorToast = true,
}: UsePatientEditPageSaveActionsArgs) => {
  const startSubmitting = useCallback(
    (action: PatientSubmittingAction) => {
      setSubmittingAction(action);
      setIsSubmitting(true);
    },
    [setIsSubmitting, setSubmittingAction]
  );

  const stopSubmitting = useCallback(() => {
    setIsSubmitting(false);
    setSubmittingAction(null);
  }, [setIsSubmitting, setSubmittingAction]);

  /**
   * Where a just-created record hands control back.
   *
   * The `patients/edit` route has to move to the new id — it was sitting on the
   * create URL, which no longer describes what is on screen. An embedder that
   * supplied `onSaved` is not a route (the sections hosted in a panel beside a
   * list), so it is told instead: navigating would unmount it mid-save.
   */
  const leaveCreateMode = useCallback(
    (saved: PatientFormData) => {
      const onSaved = onSavedRef.current;
      if (onSaved) {
        onSaved(saved);
        return;
      }
      navigate(`/${tenant}/patients/edit/${saved.id}`);
    },
    [navigate, onSavedRef, tenant]
  );

  /**
   * Tell an embedder a save landed, for the paths `leaveCreateMode` does not
   * cover — updating a record that already exists, and 一括履歴作成 on it.
   *
   * `/patients/edit/{id}` stays where it is after an update, which is right for
   * a route: the URL already describes the record. A panel over a list is not a
   * route — it has to close and let the list reload — and it has no other way
   * to learn the write succeeded. With no `onSaved` supplied this is a no-op,
   * so the route's behaviour is unchanged.
   */
  const notifySaved = useCallback(
    (saved: PatientFormData) => {
      onSavedRef.current?.(saved);
    },
    [onSavedRef]
  );

  const setBackendErrors = useCallback(
    (error: any) => {
      const beErrors = error?.response?.data?.errors;
      if (beErrors && typeof beErrors === 'object') {
        Object.entries(beErrors).forEach(([field, messages]) => {
          const msg = Array.isArray(messages) ? messages[0] : messages;
          if (msg) {
            form.setError(field as any, { type: 'manual', message: String(msg) });
          }
        });
      }
    },
    [form]
  );

  const onSubmit = useCallback(
    async (data: PatientFormSchemaType) => {
      const crossSectionValidationResult = await runAllValidations();

      if (!crossSectionValidationResult.canProceed) {
        if (crossSectionValidationResult.reason === 'error' && crossSectionErrorToast) {
          toast.error('更新ができませんでした。');
        }
        return;
      }

      const validationResult = await runValidations(data, 'official', modal);

      if (!validationResult.canProceed) {
        if (validationResult.errors.length > 0) {
          showValidationErrors(
            overrideMessage ? validationResult.errors.map(overrideMessage) : validationResult.errors,
            modal
          );
          toast.error('更新ができませんでした。');
        }
        return;
      }

      startSubmitting('submit');
      try {
        const payload = buildPatientFormPayloadForSubmit(data);
        if (isEditMode && id) {
          if (isNewHistoryMode) {
            if (
              data._bulkHistoryQualificationDate &&
              isBulkHistoryDraftUnchanged(data, initialFormValuesForGuard)
            ) {
              modal.error({
                title: BULK_HISTORY_NO_CHANGE_MESSAGE,
                okText: 'OK',
              });
              stopSubmitting();
              return;
            }

            const qualFromHidden = data._bulkHistoryQualificationDate;
            const qualFromForm = String(
              payload.health_insurance_card?.qualification_date ?? ''
            ).slice(0, 10);
            const bulkQualificationDate = qualFromHidden || qualFromForm || undefined;
            let bulkAvailableToMinus = data._bulkHistoryAvailableToMinus;
            if (bulkQualificationDate && !bulkAvailableToMinus) {
              const d = new Date(`${bulkQualificationDate}T12:00:00`);
              if (!Number.isNaN(d.getTime())) {
                d.setDate(d.getDate() - 1);
                bulkAvailableToMinus = d.toISOString().split('T')[0];
              }
            }
            mutations.bulkCreateHistory.mutate(
              {
                patientId: parseInt(id),
                data: {
                  ...payload,
                  patient: {},
                  options: {
                    ...(payload as any).options,
                    available_to_minus: bulkAvailableToMinus,
                    qualification_date: bulkQualificationDate,
                  },
                },
              },
              {
                onSuccess: result => {
                  const normalizedResult = normalizePatientData(
                    transformPatientEditToFormData(result.data)
                  );
                  setPatientData(normalizedResult);
                  syncOriginalCurrentIds(normalizedResult);
                  setInitialFormValuesForGuard(toEditFormValues(normalizedResult));
                  clearFormChange();
                  stopSubmitting();
                  setIsNewHistoryMode(false);
                  setHistorySearchParamsRef.current?.(new URLSearchParams(), { replace: true });
                  notifySaved(normalizedResult);
                },
                onError: (error: any) => {
                  stopSubmitting();
                  if (error?.response?.status === 404) {
                    authorizeNavigation();
                    navigate(`/${tenant}/patients/edit`);
                    return;
                  }
                  setBackendErrors(error);
                },
              }
            );
          } else {
            const shouldPreserveExistingHistoryContext = Boolean(
              historySearchParamsRef.current &&
                hasPatientEditHistorySearchParams(historySearchParamsRef.current)
            );
            mutations.updatePatient.mutate(
              {
                id,
                data: {
                  ...payload,
                  patient: {
                    ...(patientData?.patient?.serial_number && {
                      serial_number: patientData.patient.serial_number,
                    }),
                    official: true,
                  },
                  options: {
                    ...(payload as any).options,
                    available_to_minus: singleHistoryAvailableToMinus || undefined,
                    patient_information_history_qualification_date:
                      singleHistoryQualificationDateYmd || undefined,
                  },
                } as any,
              },
              {
                onSuccess: result => {
                  const normalizedResult = normalizePatientData(result);
                  syncOriginalCurrentIds(normalizedResult);
                  const savedHistorySearchParams = shouldPreserveExistingHistoryContext
                    ? buildExistingHistorySearchParamsFromForm(payload)
                    : null;
                  const displayResult = savedHistorySearchParams
                    ? normalizePatientData(
                        applyLegacyPatientEditSearchParams(
                          normalizedResult,
                          savedHistorySearchParams
                        )
                      )
                    : normalizedResult;
                  setPatientData(displayResult);
                  setInitialFormValuesForGuard(toEditFormValues(displayResult));
                  clearFormChange();
                  stopSubmitting();
                  setIsNewHistoryMode(false);
                  setSingleHistoryAvailableToMinus(undefined);
                  setSingleHistoryQualificationDateYmd(undefined);
                  setHistorySearchParamsRef.current?.(
                    savedHistorySearchParams ?? new URLSearchParams(),
                    {
                      replace: true,
                    }
                  );
                  queryClient.invalidateQueries({ queryKey: ['patientSearch'] });
                  notifySaved(displayResult);
                },
                onError: (error: any) => {
                  stopSubmitting();
                  if (error?.response?.status === 404) {
                    authorizeNavigation();
                    navigate(`/${tenant}/patients/edit`);
                    return;
                  }
                  setBackendErrors(error);
                },
              }
            );
          }
        } else {
          mutations.createPatient.mutate(
            {
              ...payload,
              patient: {
                serial_number: '',
                official: true,
              },
            } as any,
            {
              onSuccess: result => {
                const normalizedResult = normalizePatientData(result);
                setPatientData(normalizedResult);
                syncOriginalCurrentIds(normalizedResult);
                clearFormChange();
                stopSubmitting();
                authorizeNavigation();
                queryClient.invalidateQueries({ queryKey: ['patientSearch'] });
                leaveCreateMode(normalizedResult);
              },
              onError: () => {
                stopSubmitting();
              },
            }
          );
        }
      } catch (error) {
        console.error('Submit error:', error);
        toast.error('更新ができませんでした。');
        stopSubmitting();
      }
    },
    [
      runAllValidations,
      overrideMessage,
      crossSectionErrorToast,
      toast,
      modal,
      startSubmitting,
      stopSubmitting,
      isEditMode,
      id,
      isNewHistoryMode,
      initialFormValuesForGuard,
      mutations,
      setPatientData,
      syncOriginalCurrentIds,
      setInitialFormValuesForGuard,
      clearFormChange,
      setIsNewHistoryMode,
      setHistorySearchParamsRef,
      leaveCreateMode,
      notifySaved,
      navigate,
      tenant,
      setBackendErrors,
      historySearchParamsRef,
      patientData?.patient?.serial_number,
      singleHistoryAvailableToMinus,
      singleHistoryQualificationDateYmd,
      setSingleHistoryAvailableToMinus,
      setSingleHistoryQualificationDateYmd,
      queryClient,
      toEditFormValues,
    ]
  );

  const handleProvisionalSave = useCallback(async () => {
    form.clearErrors();

    const data = form.getValues();
    if (!validateProvisionalPatientForm(data, form.setError)) {
      toast?.error('仮登録ができませんでした。');
      return;
    }

    startSubmitting('provisional');
    try {
      const patientId = patientData?.id || null;
      const provisionalPayload = {
        ...buildPatientFormPayloadForSubmit(data),
        patient: {
          ...(patientData?.patient?.serial_number && {
            serial_number: patientData.patient.serial_number,
          }),
          official: false,
        },
        options: {
          mode: 'provisional' as const,
          available_to_minus: singleHistoryAvailableToMinus || undefined,
          patient_information_history_qualification_date:
            singleHistoryQualificationDateYmd || undefined,
        },
      };
      mutations.provisionalSave.mutate(
        {
          id: patientId,
          data: provisionalPayload as any,
        },
        {
          onSuccess: result => {
            const normalizedResult = normalizePatientData(result);
            setPatientData(normalizedResult);
            syncOriginalCurrentIds(normalizedResult);
            clearFormChange();
            stopSubmitting();
            setSingleHistoryAvailableToMinus(undefined);
            setSingleHistoryQualificationDateYmd(undefined);
            authorizeNavigation();
            queryClient.invalidateQueries({ queryKey: ['patientSearch'] });
            leaveCreateMode(normalizedResult);
          },
          onError: (error: any) => {
            stopSubmitting();
            if (error?.response?.status === 404) {
              authorizeNavigation();
              navigate(`/${tenant}/patients/edit`);
              return;
            }
          },
        }
      );
    } catch (error) {
      console.error('Provisional save error:', error);
      toast.error('仮登録に失敗しました');
      stopSubmitting();
    }
  }, [
    form,
    patientData?.id,
    patientData?.patient?.serial_number,
    mutations,
    singleHistoryAvailableToMinus,
    singleHistoryQualificationDateYmd,
    toast,
    startSubmitting,
    stopSubmitting,
    setPatientData,
    syncOriginalCurrentIds,
    clearFormChange,
    setSingleHistoryAvailableToMinus,
    setSingleHistoryQualificationDateYmd,
    queryClient,
    leaveCreateMode,
    navigate,
    tenant,
  ]);

  const handleDelete = useCallback(() => {
    modal.confirm({
      title: 'この保険証情報を削除してよろしいですか？',
      content: '',
      okText: 'OK',
      cancelText: 'キャンセル',
      onOk: () => {
        try {
          if (!id) {
            toast.error('削除対象のIDが不明です');
            return;
          }
          startSubmitting('delete');
          mutations.deletePatient.mutate(id, {
            onSuccess: () => {
              clearFormChange();
              form.reset(undefined, { keepDirty: false });
              clearLatestEditingPatientId(tenant, clinic?.clinic_number);
              queryClient.invalidateQueries({ queryKey: ['patientSearch'] });
              stopSubmitting();
              toast.success('削除が正常に終了しました。');
              setTimeout(() => {
                authorizeNavigation();
                // HIT-4829: the previous history entry is the just-deleted
                // patient's edit page (/patients/edit/{id}), which now 404s.
                // Navigate to the 保険証入力画面 (new-entry insurance screen) instead.
                navigate(`/${tenant}/patients/edit`);
              }, 500);
            },
            onError: (error: any) => {
              stopSubmitting();
              if (
                error?.response?.status === 409 ||
                error?.response?.data?.message?.includes('訪問データ')
              ) {
                toast.error('訪問データが既に存在する為、該当の保険証は削除できません。');
              } else if (error?.response?.status === 404) {
                toast.success('削除が正常に終了しました。');
                setTimeout(() => {
                  authorizeNavigation();
                  // HIT-4829: same as the success branch — avoid navigating back
                  // to the deleted patient's edit page (404).
                  navigate(`/${tenant}/patients/edit`);
                }, 500);
              } else {
                toast.error('削除に失敗しました');
              }
              console.error('Delete error:', error);
            },
          });
        } catch (error) {
          stopSubmitting();
          console.error('Delete error:', error);
          toast.error('削除に失敗しました');
        }
      },
    });
  }, [
    id,
    navigate,
    mutations,
    modal,
    toast,
    form,
    clearFormChange,
    tenant,
    clinic,
    queryClient,
    startSubmitting,
    stopSubmitting,
  ]);

  return {
    onSubmit,
    handleProvisionalSave,
    handleDelete,
  };
};
